import debug from 'debug';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { BUBBLE_SIZE_PX, CUSTOM_LAUNCHER_SIZE_PX, EXPANDED_CHAT_WIDTH, } from '@/config/constants';
import { focusTrapSelector, launcherSelector, widgetIdSelector, widgetPositionSelector, chatWindowSizeSelector, hasBubbleImageSelector, versionSelector, isWebviewOpenSelector, } from '@/state/config';
import { isMobileSelector, isWidgetOpenState, openingMessageState, } from '@/state/session';
import { IframeEventType } from '@/ts';
/**
 * Hook that sends messages to the customer page using `window.parent.postMessage`.
 */
export const useController = () => {
    const { t } = useTranslation();
    const widgetId = useRecoilValue(widgetIdSelector);
    const [isWebviewOpen, setIsWebviewOpen] = useRecoilState(isWebviewOpenSelector);
    const focusTrap = useRecoilValue(focusTrapSelector);
    const { size, type, show: showLauncher } = useRecoilValue(launcherSelector);
    const widgetPosition = useRecoilValue(widgetPositionSelector);
    const isWidgetOpen = useRecoilValue(isWidgetOpenState);
    const isMobile = useRecoilValue(isMobileSelector);
    const showOpeningMessage = useRecoilValue(openingMessageState);
    const hasBubbleImage = useRecoilValue(hasBubbleImageSelector);
    const chatWindowSize = useRecoilValue(chatWindowSizeSelector);
    const log = useMemo(() => debug(`moveo:iframe:${widgetId}:controller`), [widgetId]);
    const HELP_TEXT = t('launchers.help');
    const FEEDBACK_TEXT = t('launchers.feedback');
    const version = useRecoilValue(versionSelector);
    // Adds extra height to the widget to account for the new launcher
    const extraHeight = version === 'v2' ? '50px' : '0px';
    const sizes = useMemo(() => ({
        bubble: {
            width: `calc(${hasBubbleImage ? CUSTOM_LAUNCHER_SIZE_PX : size || BUBBLE_SIZE_PX} + 30px)`,
            height: `calc(${hasBubbleImage ? CUSTOM_LAUNCHER_SIZE_PX : size || BUBBLE_SIZE_PX} + 25px)`,
        },
        help: {
            width: `calc(${HELP_TEXT.length}ch + 80px)`,
            height: '80px',
        },
        feedback: {
            width: `calc(${FEEDBACK_TEXT.length}ch + 80px)`,
            height: '80px',
        },
        open: {
            desktop: {
                width: `calc(${chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.width} + 21px)`,
                height: `calc(${chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.height} + 24px + ${extraHeight})`,
            },
            mobile: {
                width: '100%',
                height: '100%',
            },
        },
    }), [
        FEEDBACK_TEXT.length,
        HELP_TEXT.length,
        chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.height,
        chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.width,
        hasBubbleImage,
        size,
        extraHeight,
    ]);
    // isFull is true if the iframe should take the full client's viewport
    const isFull = useMemo(() => focusTrap || widgetPosition === 'popup', [focusTrap, widgetPosition]);
    /**
     * Publish an event to the `window.parent` DOM element.
     */
    const publishEvent = useCallback((event) => {
        if (window.parent && widgetId) {
            log(`Event to web-client: ${event.type}`, event);
            window.parent.postMessage(Object.assign(Object.assign({}, event), { type: `${widgetId}${event.type}` }), '*');
        }
    }, [log, widgetId]);
    const notifyWidgetResize = useCallback((data) => {
        // When the webchat is closed and the launcher is hidden, we need to notify the parent that the iframe should be resized to 0x0
        if (!showLauncher) {
            publishEvent({
                type: IframeEventType.Resize,
                data: {
                    width: '0',
                    height: '0',
                },
            });
        }
        else {
            publishEvent({ type: IframeEventType.Resize, data });
        }
    }, [showLauncher, publishEvent]);
    /**
     * @deprecated Do not use this method. Use `closeWidget` instead.
     */
    const notifyWidgetClosed = useCallback(() => {
        if (!showOpeningMessage) {
            setTimeout(() => {
                publishEvent({
                    type: IframeEventType.Closed,
                    data: { isFull: isFull || !!isMobile },
                });
            }, 200);
            if (!showLauncher) {
                publishEvent({
                    type: IframeEventType.Resize,
                    data: {
                        width: '0',
                        height: '0',
                    },
                });
            }
            else {
                setTimeout(() => {
                    publishEvent({ type: IframeEventType.Resize, data: sizes[type] });
                }, 300);
            }
        }
    }, [
        isFull,
        isMobile,
        publishEvent,
        showLauncher,
        showOpeningMessage,
        sizes,
        type,
    ]);
    /**
     * @deprecated Do not use this method. Use `openWidget` instead.
     */
    const notifyWidgetOpened = useCallback(() => {
        const data = {
            isFull: isFull || !!isMobile,
            is_mobile: isMobile,
        };
        publishEvent({
            type: IframeEventType.Opened,
            data,
        });
        if (widgetPosition !== 'popup') {
            publishEvent({
                type: IframeEventType.Resize,
                data: sizes.open[isMobile ? 'mobile' : 'desktop'],
            });
        }
    }, [isFull, isMobile, publishEvent, sizes.open, widgetPosition]);
    const notifyWidgetExpanded = useCallback((open) => {
        const data = Object.assign(Object.assign({}, sizes.open.desktop), { width: open
                ? `calc(${EXPANDED_CHAT_WIDTH} + 20px)`
                : sizes.open.desktop.width });
        //Adds a delay so that the webchat shrinking is completed before the iframe shrinks
        const iframeResizeDelay = open ? 0 : 200;
        setTimeout(() => {
            publishEvent({
                type: IframeEventType.Resize,
                data,
            });
        }, iframeResizeDelay);
    }, [publishEvent, sizes.open]);
    const notifyUserWayInit = useCallback(() => {
        publishEvent({ type: IframeEventType.UserWayInit });
    }, [publishEvent]);
    const notifyOpeningMessageClosed = useCallback(() => {
        publishEvent({ type: IframeEventType.Resize, data: sizes[type] });
    }, [publishEvent, sizes, type]);
    const notifyWebviewOpened = useCallback(() => {
        setIsWebviewOpen(true);
        if (!isFull) {
            publishEvent({
                type: IframeEventType.Opened,
                data: { isFull: true, skipInstanceCallback: true },
            });
        }
    }, [isFull, publishEvent, setIsWebviewOpen]);
    const notifyWebviewClosed = useCallback(() => {
        setIsWebviewOpen(false);
        if (!isFull && !isMobile) {
            publishEvent({
                type: IframeEventType.Closed,
                data: { isFull: true, skipInstanceCallback: true },
            });
        }
    }, [isFull, isMobile, publishEvent, setIsWebviewOpen]);
    const notifyWidgetLoaded = useCallback(() => {
        publishEvent({ type: IframeEventType.WidgetLoaded });
    }, [publishEvent]);
    const notifySessionCreated = useCallback((data) => {
        publishEvent({
            type: IframeEventType.SessionCreated,
            data,
        });
    }, [publishEvent]);
    const notifySessionClosed = useCallback(() => {
        publishEvent({ type: IframeEventType.SessionClosed });
    }, [publishEvent]);
    const notifyMemberLeave = useCallback(() => {
        publishEvent({ type: IframeEventType.MemberLeave });
    }, [publishEvent]);
    const notifyConversationClosed = useCallback(() => {
        publishEvent({ type: IframeEventType.ConversationClosed });
    }, [publishEvent]);
    const notifySessionReconnected = useCallback((data) => {
        publishEvent({
            type: IframeEventType.SessionReconnected,
            data,
        });
    }, [publishEvent]);
    const notifyNoUnreadMessages = useCallback(() => {
        publishEvent({ type: IframeEventType.ConversationNoUnreadMessages });
    }, [publishEvent]);
    const notifyUnreadMessages = useCallback(() => {
        publishEvent({ type: IframeEventType.ConversationUnreadMessages });
    }, [publishEvent]);
    const notifyUserActivity = useCallback(() => {
        publishEvent({ type: IframeEventType.UserActivity });
    }, [publishEvent]);
    const notifyLinkClicked = useCallback((url) => {
        publishEvent({ type: IframeEventType.LinkClicked, data: { url } });
    }, [publishEvent]);
    const resizeWidget = useCallback((isMobile) => {
        if (isMobile && isWidgetOpen) {
            publishEvent({
                type: IframeEventType.Opened,
                data: { isFull: true, skipInstanceCallback: true },
            });
        }
        else if (!isMobile && !isFull && isWidgetOpen && !isWebviewOpen) {
            publishEvent({
                type: IframeEventType.Closed,
                data: { isFull: true, skipInstanceCallback: true },
            });
        }
    }, [isFull, isWidgetOpen, publishEvent, isWebviewOpen]);
    const notifyLocaleChanged = useCallback((locale) => {
        publishEvent({ type: IframeEventType.LocaleChanged, data: locale });
    }, [publishEvent]);
    return {
        notifyWidgetClosed,
        notifyWidgetOpened,
        notifyWidgetLoaded,
        notifyUserWayInit,
        notifyWidgetResize,
        notifyUserActivity,
        notifyMemberLeave,
        notifyWebviewClosed,
        notifyWebviewOpened,
        notifyLocaleChanged,
        notifyOpeningMessageClosed,
        resizeWidget,
        notifySessionCreated,
        notifySessionClosed,
        notifySessionReconnected,
        notifyNoUnreadMessages,
        notifyUnreadMessages,
        notifyConversationClosed,
        notifyLinkClicked,
        notifyWidgetExpanded,
        sizes,
    };
};
